import * as React from "react";
import { useLocation } from "react-router-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { llAtom } from "../atoms";
export type LatLng = { lat: number; lng: number };

export function parseLatLngParam(v: string | null): LatLng | null {
  if (!v) return null;
  const [latStr, lngStr] = v.split(",");
  const lat = Number(latStr?.trim());
  const lng = Number(lngStr?.trim());
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

export function useMarkerFlag(paramKey = "marker") {
  const location = useLocation();

  return React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const v = params.get(paramKey);
    return v === "1" || v === "true" || v === "yes";
  }, [location.search, paramKey]);
}
export function useMarkerText(paramKey = "markerText") {
  const location = useLocation();

  return React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get(paramKey);
    if (!raw) return null;

    const text = raw.trim();

    return text.length > 120 ? text.slice(0, 120) + "…" : text;
  }, [location.search, paramKey]);
}

function useSyncLlParam(llKey: string) {
  const location = useLocation();
  const [ll, setLl] = useAtom(llAtom);

 
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = parseLatLngParam(params.get(llKey));

  
    const same =
      (ll === null && next === null) ||
      (ll !== null &&
        next !== null &&
        ll.lat === next.lat &&
        ll.lng === next.lng);

    if (!same) setLl(next);
  }, [location.search, llKey, setLl]);

}

type Args = {
  mapLoaded: boolean;
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  zoomTo: number;
  primaryId: string | null;
  primaryLookupReady: boolean;
  hasPrimaryMatch: boolean;
  llKey?: string;
  setCenter?: (c: LatLng) => void;
  setZoom?: (z: number) => void;
};

export function useViewportFromLl({
  mapLoaded,
  mapRef,
  zoomTo,
  primaryId,
  primaryLookupReady,
  hasPrimaryMatch,
  llKey = "ll",
  setCenter,
  setZoom,
}: Args) {
  useSyncLlParam(llKey);

    const llFromUrl = useAtomValue(llAtom);
    const didRunRef = React.useRef(false);

  React.useEffect(() => {
    if (!mapLoaded) return;
    const map = mapRef.current;
    if (!map) return;
    if (!llFromUrl) return;

    if (primaryId && !primaryLookupReady) return;
    if (primaryId && hasPrimaryMatch) return;

    if (didRunRef.current) return;
    didRunRef.current = true;

    setCenter?.(llFromUrl);
    setZoom?.(zoomTo);

    requestAnimationFrame(() => {
      map.panTo(llFromUrl);
      map.setZoom(zoomTo);
    });
  }, [
    mapLoaded,
    mapRef,
    zoomTo,
    llFromUrl,
    primaryId,
    primaryLookupReady,
    hasPrimaryMatch,
    setCenter,
    setZoom,
  ]);

  React.useEffect(() => {
    didRunRef.current = false;
  }, [primaryId, llFromUrl?.lat, llFromUrl?.lng]);
}

export function useClearLl() {
  return useSetAtom(llAtom);
}
